import { AppLayout } from '@/components/layout/AppLayout'
import { Sidebar } from '@/components/layout/Sidebar'
import { CanvasArea } from '@/components/layout/CanvasArea'
import { StatusBar } from '@/components/layout/StatusBar'
import { TabBar } from '@/components/layout/TabBar'
import { ProjectHeader } from '@/components/layout/ProjectHeader'
import { EditorTopBanners } from '@/components/layout/EditorTopBanners'
import { EmptyState } from '@/components/layout/EmptyState'
import { ChevronRightIcon } from '@/components/ui/icons'
import { TableEditorModal } from '@/components/Modals/TableEditorModal'
import { DefaultsModal } from '@/components/Modals/DefaultsModal'
import { GroupsModal } from '@/components/Modals/GroupsModal'
import { ExportModal } from '@/components/Modals/ExportModal'
import { SqlImportModal } from '@/components/Modals/SqlImportModal'
import { VersionsModal } from '@/components/Modals/VersionsModal'
import { NewProjectModal } from '@/components/Modals/NewProjectModal'
import { CreateCommitModal } from '@/components/Modals/CreateCommitModal'
import { ConflictModal } from '@/components/Modals/ConflictModal'
import { OpenProjectModal } from '@/components/Modals/OpenProjectModal'
import { ShortcutsModal } from '@/components/Modals/ShortcutsModal'
import { FKPanel } from '@/components/Panels/FKPanel'
import { IndexPanel } from '@/components/Panels/IndexPanel'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useKeyboardShortcuts } from '@/hooks/useKeyboardShortcuts'
import { useWorkspaceSync } from '@/hooks/useWorkspaceSync'
import { useAutosave } from '@/hooks/useAutosave'
import { useSettingsStore } from '@/stores'
import { useTabsStore } from '@/stores/tabsStore'
import { useModalStore } from '@/hooks/useModalStore'
import { CanvasGuide } from '@/components/Help/CanvasGuide'
import { hasSeenCanvasGuide } from '@/utils/canvasGuideStorage'

export function AppShell() {
  const { t } = useTranslation()
  useKeyboardShortcuts()
  useWorkspaceSync()
  useAutosave()

  const hasTabs = useTabsStore(s => s.tabs.length > 0)
  const sidebarHidden = useSettingsStore((s) => s.sidebarHidden)
  const toggleSidebar = useSettingsStore((s) => s.toggleSidebar)
  const conflict = useModalStore((s) => s.conflict)
  const closeConflict = useModalStore((s) => s.closeConflict)
  const openProjectPickerOpen = useModalStore((s) => s.openProjectPickerOpen)
  const closeOpenProjectPicker = useModalStore((s) => s.closeOpenProjectPicker)
  const openCanvasGuide = useModalStore((s) => s.openCanvasGuide)

  useEffect(() => {
    if (hasTabs && !hasSeenCanvasGuide()) openCanvasGuide()
  }, [hasTabs, openCanvasGuide])

  return (
    <div className="flex flex-1 min-h-0 flex-col overflow-hidden">
      <AppLayout
        tabBar={<TabBar />}
        sidebar={hasTabs ? (
          <div
            data-guide="sidebar"
            style={{
              width: sidebarHidden ? 0 : 240,
              flexShrink: 0,
              overflow: 'hidden',
              transition: 'width 200ms ease',
            }}
          >
            <Sidebar />
          </div>
        ) : null}
        canvas={hasTabs ? (
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            <ProjectHeader />
            <div className="relative flex min-h-0 flex-1 overflow-hidden">
              <EditorTopBanners />
              <button
                onClick={toggleSidebar}
                title={sidebarHidden ? t('app.expandPanel') : t('app.collapsePanel')}
                aria-label={sidebarHidden ? t('app.expandPanel') : t('app.collapsePanel')}
                className="sidebar-toggle-hint absolute left-0 top-1/2 z-20 -translate-y-1/2 flex items-center justify-center"
                style={{
                  width: 20,
                  height: 54,
                  background: 'var(--bg-sidebar)',
                  border: '1px solid var(--border)',
                  borderLeft: 'none',
                  borderRadius: '0 6px 6px 0',
                  color: 'var(--accent)',
                  cursor: 'pointer',
                }}
              >
                <span style={{ display: 'flex', transform: sidebarHidden ? undefined : 'rotate(180deg)' }}>
                  <ChevronRightIcon />
                </span>
              </button>
              <CanvasArea />
            </div>
          </div>
        ) : <EmptyState />}
        statusBar={hasTabs ? <StatusBar /> : null}
      />
      <TableEditorModal />
      <DefaultsModal />
      <GroupsModal />
      <ExportModal />
      <NewProjectModal />
      <CreateCommitModal />
      <ConflictModal
        open={conflict.open}
        onClose={closeConflict}
        currentCommitId={conflict.currentCommitId}
      />
      <OpenProjectModal open={openProjectPickerOpen} onClose={closeOpenProjectPicker} />
      <SqlImportModal />
      <VersionsModal />
      <ShortcutsModal />
      <FKPanel />
      <IndexPanel />
      <CanvasGuide />
    </div>
  )
}
